import { DEFAULT_TASK_WORKFLOW_STATE, type WorkflowColumn } from '@portfolio-pm/shared';
import { newId } from '@portfolio-pm/shared';
import * as Y from 'yjs';
import {
  type WithNow,
  ensureDefaultWorkflowColumns,
  getDeletedMap,
  getTasksMap,
  getWorkflowColumnsMap,
} from './doc.ts';

export interface CreateWorkflowColumnInput {
  name: string;
}

function ts(options: WithNow): number {
  return options.now ?? Date.now();
}

export function listWorkflowColumns(doc: Y.Doc): WorkflowColumn[] {
  ensureDefaultWorkflowColumns(doc);
  const out: WorkflowColumn[] = [];
  for (const [id, inner] of getWorkflowColumnsMap(doc).entries()) {
    out.push(workflowColumnFromMap(id, inner));
  }
  return out.sort((a, b) => a.order - b.order || a.createdAt - b.createdAt);
}

export function getWorkflowColumn(doc: Y.Doc, id: string): WorkflowColumn | undefined {
  const inner = getWorkflowColumnsMap(doc).get(id);
  return inner ? workflowColumnFromMap(id, inner) : undefined;
}

export function createWorkflowColumn(
  doc: Y.Doc,
  input: CreateWorkflowColumnInput,
  options: WithNow = {},
): WorkflowColumn {
  const now = ts(options);
  const columns = getWorkflowColumnsMap(doc);
  const id = newId();
  const maxOrder = Math.max(-1, ...listWorkflowColumns(doc).map((column) => column.order));
  const inner = new Y.Map<unknown>();
  inner.set('name', input.name);
  inner.set('order', maxOrder + 1);
  inner.set('createdAt', now);
  inner.set('updatedAt', now);
  columns.set(id, inner);
  return workflowColumnFromMap(id, inner);
}

export function renameWorkflowColumn(
  doc: Y.Doc,
  id: string,
  name: string,
  options: WithNow = {},
): void {
  const inner = getWorkflowColumnsMap(doc).get(id);
  if (!inner) return;
  inner.set('name', name);
  inner.set('updatedAt', ts(options));
}

export function reorderWorkflowColumns(
  doc: Y.Doc,
  orderedIds: string[],
  options: WithNow = {},
): void {
  const columns = getWorkflowColumnsMap(doc);
  const now = ts(options);
  doc.transact(() => {
    orderedIds.forEach((id, index) => {
      const inner = columns.get(id);
      if (!inner) return;
      inner.set('order', index);
      inner.set('updatedAt', now);
    });
  });
}

export function deleteWorkflowColumn(
  doc: Y.Doc,
  id: string,
  replacementId = DEFAULT_TASK_WORKFLOW_STATE,
  options: WithNow = {},
): void {
  if (id === DEFAULT_TASK_WORKFLOW_STATE) return;
  const columns = getWorkflowColumnsMap(doc);
  if (!columns.has(id) || !columns.has(replacementId)) return;
  const now = ts(options);

  doc.transact(() => {
    const tasks = getTasksMap(doc);
    for (const inner of tasks.values()) {
      if (inner.get('workflowState') === id) {
        inner.set('workflowState', replacementId);
        inner.set('updatedAt', now);
      }
    }
    columns.delete(id);
    getDeletedMap(doc).set(id, now);
    normalizeWorkflowColumnOrder(doc, now);
  });
}

export function normalizeWorkflowColumnOrder(doc: Y.Doc, now = Date.now()): void {
  const columns = [...getWorkflowColumnsMap(doc).entries()]
    .map(([id, inner]) => ({ id, inner, column: workflowColumnFromMap(id, inner) }))
    .sort((a, b) => a.column.order - b.column.order || a.column.createdAt - b.column.createdAt);

  columns.forEach(({ inner }, index) => {
    inner.set('order', index);
    inner.set('updatedAt', now);
  });
}

function workflowColumnFromMap(id: string, inner: Y.Map<unknown>): WorkflowColumn {
  const column: WorkflowColumn = {
    id,
    name: String(inner.get('name') ?? ''),
    order: Number(inner.get('order') ?? 0),
    createdAt: Number(inner.get('createdAt') ?? 0),
    updatedAt: Number(inner.get('updatedAt') ?? 0),
  };

  if (inner.get('protected') === true) column.protected = true;
  return column;
}
